import plusImg from '../../../assets/images/icons/plus.svg'
import minusImg from '../../../assets/images/icons/minus.svg'
import downArrowImg from '../../../assets/images/icons/downArrow.svg'
import { useCallback, useContext, useEffect, useState } from 'react'
import { sampleDefault, SamplesContext, SamplesResponseType, UpdateSamplesContext } from '../../../context/samplesContext'
import { Modal } from '../../../components/Modal'
import { useParams } from 'react-router-dom'
import { SizeCandidateInput } from '../../../components/input/SizeCandidateField'
import { useCanEdit } from '../../../hooks/useCanEdit'
import { useTranslation } from 'react-i18next'

const LINKING_IDENTIFIER_SANITIZE_PATTERN = /[^A-Za-z0-9_-]/g
const SIZE_MAX_LENGTH = 255
const FINAL_SAMPLE_TIME = 1
const FINAL_SAMPLE_TIME_DISPLAY_VALUE = 'final'
const SAMPLE_TIME_PLACEHOLDER_VALUE = ''
const SAMPLE_TIME_OPTIONS = [1, 2, 3, 4, 5] as const
const SAMPLE_TIME_LABELS: Record<typeof SAMPLE_TIME_OPTIONS[number], string> = {
  1: '1st',
  2: '2nd',
  3: '3rd',
  4: '4th',
  5: '5th'
}
const FIELD_LABEL_CLASS = 'flex justify-between items-center gap-[8px] mb-[8px] w-full'
const FIELD_LABEL_WITHOUT_FULL_WIDTH_CLASS = 'flex justify-between items-center gap-[8px] mb-[8px]'
const FIELD_NAME_CLASS = 'text-white text-[11px] leading-[1.15] hidden desktop-xmd:block desktop-xmd:max-w-[74px]'
const FIELD_BUTTON_CLASS = 'min-w-[100px] w-full h-[36px] rounded-[5px] pl-[10px] bg-white flex items-center desktop-xmd:max-w-[190px] desktop-xmd:w-full'
const FIELD_TEXT_CLASS = 'text-xs desktop-xmd:text-[13px]'
const TEXT_INPUT_CLASS = 'min-w-[140px] w-full h-[36px] rounded-[5px] pl-[10px] text-xs desktop-xmd:max-w-[190px] desktop-xmd:text-[13px]'
const SAMPLE_TIME_WRAPPER_CLASS = 'relative min-w-[90px] h-[36px] desktop-xmd:max-w-[190px] desktop-xmd:w-full'
const SAMPLE_TIME_PLACEHOLDER_CLASS = 'pointer-events-none absolute left-[10px] top-1/2 -translate-y-1/2 text-gray-400'
const SAMPLE_TIME_SELECT_BASE_CLASS = 'w-full h-full appearance-none rounded-[5px] bg-white pl-[10px] pr-[28px] disabled:opacity-100'
const SAMPLE_TIME_ARROW_CLASS = 'pointer-events-none absolute right-0 top-0 flex h-full w-[28px] items-center justify-center rounded-r-[5px]'

const sanitizeLinkingIdentifier = (value: string): string => {
  return value.replace(LINKING_IDENTIFIER_SANITIZE_PATTERN, '')
}

const isSampleTimeOption = (value: number): value is typeof SAMPLE_TIME_OPTIONS[number] => {
  return SAMPLE_TIME_OPTIONS.some((option) => option === value)
}

export function InputContainer({ setIsUpdate }: { setIsUpdate: React.Dispatch<React.SetStateAction<boolean>> }) {
  const { t } = useTranslation()
  const { rowSample } = useContext(SamplesContext)
  const { updateRowSample, resetCurrentSample, decrement, increment, loadMeasurementComparison } = useContext(UpdateSamplesContext)
  const { canEdit } = useCanEdit()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)
  const [errorModalMessage, setErrorModalMessage] = useState(t('messages.sampleNotFound'))
  const openModal = useCallback(() => { setIsModalOpen(true) }, [])
  const closeModal = useCallback(() => { setIsModalOpen(false) }, [])
  const closeErrorModal = useCallback(() => { setIsErrorModalOpen(false) }, [])
  const [modalType, setModalType] = useState<'part_number' | 'sample_number'>()
  const { sampleId } = useParams()
  const isFinal = rowSample.kind === 'final'
  const displayedSampleTime = rowSample.time
  const sampleTimeSelectValue = isFinal
    ? FINAL_SAMPLE_TIME_DISPLAY_VALUE
    : displayedSampleTime === 0
      ? SAMPLE_TIME_PLACEHOLDER_VALUE
      : displayedSampleTime
  const isSampleTimeDisabled = !canEdit || isFinal

  useEffect(() => {
    const requestedSampleId = sampleId?.trim()
    if (!requestedSampleId) {
      setIsErrorModalOpen(false)
      resetCurrentSample()
      return
    }

    let isCancelled = false

    const loadSample = async () => {
      const result = await loadMeasurementComparison(requestedSampleId)
      if (isCancelled) {
        return
      }

      if (result.status === 'success') {
        setIsErrorModalOpen(false)
        return
      }

      if (result.status === 'not_found') {
        resetCurrentSample()
        setErrorModalMessage(t('messages.sampleNotFound'))
        setIsErrorModalOpen(true)
        return
      }

      resetCurrentSample()
      setErrorModalMessage(t('messages.sampleFetchFailed'))
      setIsErrorModalOpen(true)
    }

    void loadSample()

    return () => {
      isCancelled = true
    }
  }, [sampleId, loadMeasurementComparison, resetCurrentSample, t])

  return (
    <div>
      <div className="flex mb-[15px] flex-col">
        <div className='flex space-x-[5px] justify-between desktop-xmd:contents desktop-xmd:space-x-0'>
          <div className='flex mb-[12px] space-x-[5px] desktop-xmd:justify-between'>
            <label className="flex items-center text-white text-[11px] min-w-[66px] desktop-xmd:text-[12px]">
              <span className='min-w-fit'>{t('sample.final')}</span>
              <input
                type="radio"
                name="type"
                className="ml-[5px] w-[32px] h-[32px] accent-[#545899]"
                checked={isFinal}
                onChange={() => { if (canEdit) updateRowSample(({ kind: 'final', time: FINAL_SAMPLE_TIME })) }}
                onClick={(e) => { if (!canEdit) e.preventDefault() }}
                aria-disabled={!canEdit}
              />
            </label>
            <label className="flex items-center text-white text-[11px] min-w-[85px] desktop-xmd:text-[12px]">
              <span className='min-w-fit'>{t('sample.sample')}</span>
              <input
                type="radio"
                name="type"
                className="ml-[5px] w-[32px] h-[32px] accent-[#545899]"
                checked={rowSample.kind === 'sample'}
                onChange={() => { if (canEdit) updateRowSample(({ kind: 'sample' })) }}
                onClick={(e) => { if (!canEdit) e.preventDefault() }}
                aria-disabled={!canEdit}
              />
            </label>
          </div>
          <div className="flex items-center justify-between mb-[8px] min-w-[155px]">
            <span className="text-white text-[11px] leading-[1.15] min-w-fit desktop-xmd:text-[12px]">{t('sample.repeat')}</span>
            <div className="flex items-center justify-between bg-white w-[100px] h-[36px] rounded-[5px] desktop-xmd:w-[129px]">
              <button
                className={`w-[25px] h-[25px] rounded-full ml-[18px] ${!canEdit ? 'cursor-auto' : ''}`}
                onClick={() => {
                  if (!canEdit) return
                  if (rowSample.repeat <= 0) return
                  decrement()
                }}
                aria-disabled={!canEdit}
              >
                <img src={minusImg} alt='minus' />
              </button>
              <span className={`${rowSample.repeat === 0 ? 'text-[#B1B1B1]' : 'text-black'}`}>{rowSample.repeat}</span>
              <button
                className={`w-[25px] h-[25px] rounded-full mr-[18px] ${!canEdit ? 'cursor-auto' : ''}`}
                onClick={() => { if (canEdit) increment() }}
                aria-disabled={!canEdit}
              >
                <img src={plusImg} alt='plue' />
              </button>
            </div>
          </div>
          <label className={FIELD_LABEL_CLASS}>
            <span className={FIELD_NAME_CLASS}>{t('sample.partNumber')}</span>
            <button
              onClick={() => {
                if (!canEdit) return
                setModalType('part_number')
                openModal()
              }}
              className={`${FIELD_BUTTON_CLASS} ${!canEdit ? 'cursor-auto' : ''}`}
              aria-disabled={!canEdit}
            >
              <span className={`${rowSample.part_number ? 'text-black' : 'text-gray-400'} ${FIELD_TEXT_CLASS}`}>{rowSample.part_number || t('placeholders.partNumberInput')}</span>
            </button>
          </label>
          <label className={FIELD_LABEL_CLASS}>
            <span className={FIELD_NAME_CLASS}>{t('sample.sampleNumber')}</span>
            <button
              onClick={() => {
                if (!canEdit) return
                setModalType('sample_number')
                openModal()
              }}
              className={`${FIELD_BUTTON_CLASS} ${!canEdit ? 'cursor-auto' : ''}`}
              aria-disabled={!canEdit}
            >
              <span className={`${rowSample.sample_number ? 'text-black' : 'text-gray-400'} ${FIELD_TEXT_CLASS}`}>{rowSample.sample_number || t('placeholders.sampleNumberInput')}</span>
            </button>
          </label>
          <label className={FIELD_LABEL_WITHOUT_FULL_WIDTH_CLASS}>
            <span className={FIELD_NAME_CLASS}>1st/2nd</span>
            <div className={SAMPLE_TIME_WRAPPER_CLASS}>
              {!isFinal && displayedSampleTime === 0 && (
                <span className={SAMPLE_TIME_PLACEHOLDER_CLASS}>
                  1st
                </span>
              )}
              <select
                className={`${SAMPLE_TIME_SELECT_BASE_CLASS} ${!isFinal && displayedSampleTime === 0 ? 'text-transparent' : 'text-black'} ${isFinal ? 'cursor-not-allowed bg-gray-100 text-gray-500' : ''}`}
                value={sampleTimeSelectValue}
                disabled={isSampleTimeDisabled}
                onChange={(e) => {
                  if (isSampleTimeDisabled) return

                  const selectedTime = Number(e.target.value)
                  if (!isSampleTimeOption(selectedTime)) return

                  updateRowSample(({ time: selectedTime }))
                }}
                aria-disabled={isSampleTimeDisabled}
              >
                {isFinal && <option value={FINAL_SAMPLE_TIME_DISPLAY_VALUE}>-</option>}
                <option value={SAMPLE_TIME_PLACEHOLDER_VALUE} disabled hidden>1st</option>
                {SAMPLE_TIME_OPTIONS.map((time) => (
                  <option value={time} key={time} className="text-black">{SAMPLE_TIME_LABELS[time]}</option>
                ))}
              </select>
              <span
                className={SAMPLE_TIME_ARROW_CLASS}
                aria-hidden="true"
              >
                <img src={downArrowImg} alt="" className="h-[6px] w-[11px]" />
              </span>
            </div>
          </label>
          <label className={FIELD_LABEL_CLASS}>
            <span className={FIELD_NAME_CLASS}>{t('sample.size')}</span>
            <SizeCandidateInput
              placeholder={t('placeholders.sizeInput')}
              value={rowSample.size || ''}
              maxLength={SIZE_MAX_LENGTH}
              onChange={(value) => { if (canEdit) updateRowSample({ size: value }) }}
              readOnly={!canEdit}
              fieldWrapperClassName="min-w-[90px] h-[36px] desktop-xmd:max-w-[190px] w-full"
              inputClassName="h-full rounded-[5px] border-0 bg-white pl-[10px] pr-[28px] text-xs shadow-none desktop-xmd:text-[13px]"
              toggleButtonClassName="w-[28px] rounded-r-[5px] border-[#D9D9D9]"
              dropdownClassName="rounded-[5px] p-[8px]"
              optionClassName="px-[8px] py-[4px] text-[12px]"
            />
          </label>
        </div>
        <div className='flex space-x-[5px] desktop-xmd:contents desktop-xmd:space-x-0'>
          <label className={FIELD_LABEL_CLASS}>
            <span className={FIELD_NAME_CLASS}>{t('sample.color')}</span>
            <input
              type="text"
              placeholder={t('placeholders.colorInput')}
              className={TEXT_INPUT_CLASS}
              value={rowSample.color_no}
              onChange={(e) => { if (canEdit) updateRowSample(({ color_no: e.target.value })) }}
              readOnly={!canEdit}
            />
          </label>
          <label className={FIELD_LABEL_CLASS}>
            <span className={FIELD_NAME_CLASS}>{t('sample.item')}</span>
            <input
              type="text"
              placeholder={t('placeholders.itemNameInput')}
              className={TEXT_INPUT_CLASS}
              value={rowSample.item_name || ''}
              onChange={(e) => { if (canEdit) updateRowSample(({ item_name: e.target.value })) }}
              readOnly={!canEdit}
            />
          </label>
          <label className={FIELD_LABEL_CLASS}>
            <span className={FIELD_NAME_CLASS}>{t('sample.brand')}</span>
            <input
              type="text"
              placeholder={t('placeholders.brandNameInput')}
              className={TEXT_INPUT_CLASS}
              value={rowSample.brand_name || ''}
              onChange={(e) => { if (canEdit) updateRowSample(({ brand_name: e.target.value })) }}
              readOnly={!canEdit}
            />
          </label>
          <div className="flex items-center justify-between gap-[8px] min-w-[135px] desktop-xmd:mb-[8px]">
            <span className="text-white text-[11px] leading-[1.15] min-w-fit desktop-xmd:max-w-[86px]">{t('sample.identificationResult')}</span>
            <input
              placeholder={t('placeholders.pants')}
              type="text"
              className="w-20 h-[36px] pl-[10px] rounded-[5px] text-xs desktop-xmd:w-[118px] desktop-xmd:text-[13px]"
              value={rowSample.clothtype || ''}
              readOnly
            />
          </div>
          <div className='flex items-center justify-between gap-[8px] min-w-[155px]'>
            <span className='text-white text-[11px] leading-[1.15] min-w-fit desktop-xmd:max-w-[86px]'>{t('sample.weightResult')}</span>
            <input
              placeholder='100'
              type='text'
              className='w-20 h-[36px] rounded-[5px] pl-[10px] text-xs desktop-xmd:w-[118px] desktop-xmd:text-[13px]'
              value={rowSample.sampleWeightMeasurements[0]?.weight || ''}
              readOnly
            />
          </div>
        </div>
      </div>
      <LinkingModal isOpen={isModalOpen} modalType={modalType} onClose={closeModal} setIsUpdate={setIsUpdate} />
      <SampleNotFoundModal isOpen={isErrorModalOpen} onClose={closeErrorModal} message={errorModalMessage} />
    </div>
  )
}

function SampleNotFoundModal({ isOpen, onClose, message }: { isOpen: boolean, onClose: () => void, message: string }) {
  const { t } = useTranslation()

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="w-[500px] h-[200px] flex flex-col items-center justify-center px-[25px]">
      <span className="text-black text-center">
        {message}
      </span>
      <button
        className="mt-[18px] w-[160px] h-[47px] border-[1.5px] border-[#C5C7FF] rounded-[50px] text-[#545899] font-bold"
        onClick={onClose}
      >
        {t('common.close')}
      </button>
    </Modal>
  )
}

function LinkingModal({ isOpen, modalType, onClose, setIsUpdate }: { isOpen: boolean, modalType?: string, onClose: () => void, setIsUpdate: React.Dispatch<React.SetStateAction<boolean>> }) {
  const { t } = useTranslation()
  const { rowSample, samples } = useContext(SamplesContext)
  const { updateRowSample } = useContext(UpdateSamplesContext)

  const [relatedSamples, setRelatedSamples] = useState<SamplesResponseType[]>([])
  const [selectedSample, setSelectedSample] = useState<SamplesResponseType>(sampleDefault)
  const [modalChildrenType, setModalChildrenType] = useState<'link' | 'confirm'>('link')

  const [value, setValue] = useState<string>('')

  useEffect(() => {
    if (modalType === 'part_number') {
      setValue(rowSample.part_number || '')
    } else if (modalType === 'sample_number') {
      setValue(rowSample.sample_number || '')
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  useEffect(() => {
    setModalChildrenType('link')
    setSelectedSample(sampleDefault)
  }, [isOpen])

  useEffect(() => {
    let filteredSamples: SamplesResponseType[] = [];

    if (modalType === 'part_number') {
      if (value) {
        const group = samples.filter(
          el =>
            el.sample_number === rowSample.sample_number &&
            el.part_number !== null &&
            el.part_number !== '' &&
            el.id !== rowSample.id
        )

        const allEmptyPart = group.every(el => !el.part_number)
        if (allEmptyPart) {
          filteredSamples = []
        } else if (value) {
          filteredSamples = group.filter(el =>
            el.part_number?.toLowerCase().includes(value.toLowerCase())
          )
        } else {
          filteredSamples = []
        }
      } else {
        filteredSamples = []
      }
    }
    else if (modalType === 'sample_number') {
      if (value) {
        filteredSamples = samples.filter(
          el =>
            el.sample_number?.toLowerCase().includes(value.toLowerCase()) &&
            el.id !== rowSample.id
        )
      } else {
        filteredSamples = []
      }
    }

    // ─────────── 空レコード除外 ───────────
    filteredSamples = filteredSamples.filter(
      el =>
        (el.sample_number !== null && el.sample_number !== '') ||
        (el.part_number !== null && el.part_number !== '')
    );

    // ─────────── 重複除外（最新レコードを優先） ───────────
    const deduped = Object.values(
      filteredSamples.reduce<Record<string, SamplesResponseType>>((acc, cur) => {
        const key = modalType === 'part_number'
          ? cur.part_number!
          : cur.sample_number!;

        const prev = acc[key];
        if (!prev) {
          acc[key] = cur;
        } else {
          const prevDate = prev.updated_at !== 'batch'
            ? new Date(prev.updated_at)
            : new Date(prev.created_at);
          const curDate = cur.updated_at !== 'batch'
            ? new Date(cur.updated_at)
            : new Date(cur.created_at);

          if (curDate > prevDate) {
            acc[key] = cur;
          }
        }
        return acc;
      }, {})
    );

    setRelatedSamples(deduped);
  }, [value, samples, modalType, rowSample]);

  const linkedSample = useCallback(() => {
    const propertiesToUpdate: (keyof SamplesResponseType)[] = [
      'part_number',
      'sample_number',
      'item_name',
      'repeat',
      'brand_name'
    ];

    (Object.keys(rowSample) as (keyof SamplesResponseType)[]).forEach((key) => {
      if (
        propertiesToUpdate.includes(key) &&
        (rowSample[key] === null || rowSample[key] === '' || rowSample[key] === 0)
      ) {
        updateRowSample(({ [key]: selectedSample[key] }))
      }
    })
  }, [rowSample, selectedSample, updateRowSample])

  const onClickLink = useCallback(() => {
    if (modalType === 'part_number') {
      const newValue = selectedSample.part_number ? selectedSample.part_number : value
      setModalChildrenType('confirm')
      updateRowSample({ part_number: newValue })
    } else if (modalType === 'sample_number') {
      const newValue = selectedSample.sample_number ? selectedSample.sample_number : value
      linkedSample()
      updateRowSample({ sample_number: newValue })
      onClose()
    }
  }, [modalType, linkedSample, onClose, updateRowSample, value, selectedSample])

  const onClickConfirm = useCallback((bol: boolean) => {
    if (bol) {
      linkedSample()
      setIsUpdate(true)
    } else {
      linkedSample()
      setIsUpdate(false)
    }
    onClose()
  }, [linkedSample, onClose, setIsUpdate])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className={modalChildrenType === 'link' ? 'flex flex-col w-[600px] h-[600px] px-5 py-[15px]' : 'flex flex-col w-[500px] h-[300px] px-5 py-[15px]'}
    >
      {modalChildrenType === 'link' ? (
        <>
          <div className='flex items-center w-full'>
            <div className='w-2 h-[35px] rounded-sm bg-[#3E3E3E] mr-[5px]' ></div>
            <span className='text-[#3E3E3E] text-[16px]'>
              {modalType === 'part_number' ? t('placeholders.partNumberInput') : t('placeholders.sampleNumberInput')}
            </span>
          </div>
          <div className='mt-[25px]'>
            <input
              className='w-full h-[40px] rounded-[5px] pl-[10px] border-[#545899] border-[2px] outline-none'
              placeholder={modalType === 'part_number' ? t('placeholders.partNumberInput') : t('placeholders.sampleNumberInput')}
              value={
                modalType === 'part_number' ?
                  (selectedSample.part_number ? selectedSample.part_number : value)
                  : (selectedSample.sample_number ? selectedSample.sample_number : value)
              }
              onChange={(e) => { setValue(sanitizeLinkingIdentifier(e.target.value)) }}
            />
          </div>
          <div className='flex flex-col h-full mt-[15px]'>
            <div className='flex flex-col h-[300px]'>
              <span>{t('sample.pastSamples')}</span>
              <div className='w-full h-full overflow-y-auto ml-[5px] mt-[5px]'>
                {relatedSamples.length === 0 ? (
                  <span className='text-[14px]'>
                    {t('messages.relatedSampleNotFound')}
                  </span>
                ) :
                  relatedSamples.map((el, index) => (
                    <label className='flex items-center' key={index}>
                      <input type='radio' name='linked' className='w-[12px] h-[12px] accent-[#545899]' onClick={() => setSelectedSample(el)} />
                      <span className='ml-[5px] text-[14px]'>
                        {modalType === 'part_number' ? `${el.part_number} Repeat: ${el.repeat}` : `${el.sample_number} Repeat: ${el.repeat}`}
                      </span>
                    </label>
                  ))}
              </div>
            </div>
          </div>
          <div className='flex justify-end w-full mt-[25px]'>
            <button
              onClick={() => {
                onClickLink()
              }}
              className='w-[160px] h-[47px] bg-[#C5C7FF] rounded-[50px] text-[#545899] font-bold'
            >
              {t('common.confirm')}
            </button>
          </div>
        </>
      ) : (
        <div className='flex flex-col w-full h-full items-center justify-center'>
          <div>{t('messages.linkPastSamplesConfirm')}</div>
          <span className='text-[12px]'>{t('messages.linkPastSamplesNote')}</span>
          <div className='mt-[45px] space-x-[25px]'>
            <button
              className='border border-[#C5C7FF] rounded-[50px] w-[150px] h-[50px] text-[#545899] font-semibold'
              onClick={() => onClickConfirm(false)}
            >
              {t('common.no')}
            </button>
            <button
              className='rounded-[50px] w-[150px] h-[50px] bg-[#C5C7FF] text-[#545899] font-semibold'
              onClick={() => onClickConfirm(true)}
            >
              {t('common.yes')}
            </button>
          </div>
        </div>
      )}
    </Modal>
  )
}
